import React, { useState } from 'react';
import { observer } from 'mobx-react-lite';
import { styled } from 'dripsy';
import { Trans, useTranslation } from 'react-i18next';
import { View } from 'react-native';
import {
  StackActions,
  useNavigation,
  useRoute,
} from '@react-navigation/native';

import {
  Button,
  Separator,
  PaymentForm,
  BaseText,
  ButtonsLayout as BaseButtonsLayout,
  ButtonStyleEnum,
  BaseTextLink,
  CheckBox,
} from '@components';
import { usePaymentMethod } from '@hooks';
import { ToastService } from '@services';
import {
  FeedbackNavigatorNavigationPropType,
  FeedbackNavigatorRoutePropType,
  screens,
} from '@navigation';
import { parseApiError } from '@utils';
import { useTracksModel } from '@models/Tracks/TracksModel';
import { ConfirmPaymentTypeEnum, synthoURLs } from '@constants';
import { useViewerModel } from '@models/Viewer/ViewerModel';

const TermsText = styled(BaseText)({
  flexShrink: 1,
  color: '$heading800',
  variant: 'text.small',
  ml: '$5',
});

const TextLink = styled(BaseTextLink)({
  variant: 'text.small',
});

const RowContainer = styled(View)({
  flexDirection: 'row',
  alignItems: 'flex-start',
});

const ButtonsLayout = styled(BaseButtonsLayout)({
  width: 165,
});

type RoutePropType = FeedbackNavigatorRoutePropType<'CreditsPayment'>;
type NavigationPropType =
  FeedbackNavigatorNavigationPropType<'CreditsPayment'>;

export const CreditsPaymentForm = observer(() => {
  const { t } = useTranslation();
  const route = useRoute<RoutePropType>();
  const nav = useNavigation<NavigationPropType>();
  const tracks = useTracksModel();
  const viewer = useViewerModel();
  const paymentMethod = usePaymentMethod();
  const [isPurchasing, setIsPurchasing] = useState(false);
  const [isAgreed, setIsAgreed] = useState(false);
  const [isPaymentFormComplete, setIsPaymentFormComplete] =
    useState(false);

  function navigateToTerms() {
    window.open(synthoURLs.TERMS_AND_CONDITIONS, '_blank');
  }

  function navigateToPrivacy() {
    window.open(synthoURLs.PRIVACY_POLICY, '_blank');
  }

  function cancelAction() {
    if (nav.canGoBack()) {
      nav.goBack();
    } else {
      nav.dispatch(StackActions.replace(screens.TrackFeedback));
    }
  }

  async function purchase() {
    try {
      setIsPurchasing(true);

      await paymentMethod.submit();

      const createPaymentIntentRes =
        await viewer.createCreditsPaymentIntent.run({
          creditsOfferId: route.params.id,
        });

      if (!createPaymentIntentRes) {
        throw new Error();
      }

      const paymentIntent = await paymentMethod.confirmPayment({
        clientSecret: createPaymentIntentRes.clientSecret,
        queryParams: {
          type: ConfirmPaymentTypeEnum.Credits.toString(),
        },
      });

      if (!paymentIntent) {
        throw new Error();
      }

      await viewer.confirmPayment.run({
        paymentIntentId: paymentIntent.id,
      });

      await tracks.feedback.fetchStatus.run();

      cancelAction();
      ToastService.showSuccess(t('creditsPayment.successMessage'));
      setIsPurchasing(false);
    } catch (error) {
      setIsPurchasing(false);
      ToastService.showError(parseApiError(error).message);
    }
  }

  const canBePurchased = isPaymentFormComplete && isAgreed;

  return (
    <View>
      <PaymentForm onChangeStatus={setIsPaymentFormComplete} />
      <Separator />
      <RowContainer>
        <CheckBox value={isAgreed} onValueChange={setIsAgreed} />
        <TermsText>
          <Trans
            i18nKey="creditsPayment.agreement"
            components={{
              termsLink: <TextLink onPress={navigateToTerms} />,
              privacyLink: <TextLink onPress={navigateToPrivacy} />,
            }}
          />
        </TermsText>
      </RowContainer>

      <Separator />

      <ButtonsLayout>
        <Button
          onPress={purchase}
          isLoading={isPurchasing}
          disabled={!canBePurchased}
        >
          {t('creditsPayment.pay')}
        </Button>

        <Button
          onPress={cancelAction}
          buttonStyle={ButtonStyleEnum.Outline}
        >
          {t('creditsPayment.cancel')}
        </Button>
      </ButtonsLayout>
    </View>
  );
});
